import { Roles } from '@/entities/roles'
import { SortDirection } from '@/entities/sort'
import { USER_ROLE_KEY } from '@/features/auth'
import {
  AppealsDialog,
  CreateWithdrawDialog,
  EditPayoutDialog,
  getColumns,
  MobileBlockMerchantWithdraw,
  PayoutsSettingsBanner,
  useWithdrawsMerchant,
  useWithdrawsModal,
  TraderWithdrawFilterStatus,
} from '@/features/withdraws'
import { useWithdrawsTrader } from '@/features/withdraws/hooks/useWithdrawsTrader'
import { FiltersWithdraws, WithdrawsStateType } from '@/features/withdraws/ui/filters'
import { AcceptedPanel } from '@/features/withdraws/ui/tabPanelsTrader/AcceptedPanel'
import { AssignedPanel } from '@/features/withdraws/ui/tabPanelsTrader/AssignedPanel'
import { CompletedPanel } from '@/features/withdraws/ui/tabPanelsTrader/CompletedPanel'
import PenSettingsIcon from '@/shared/assets/icons/payout/penSettings.svg?react'
import { BaseButton, BaseTabs } from '@/shared/components'
import { PageTitle } from '@/shared/components/PageTitle/PageTitle'
import { SearchInput } from '@/shared/components/SearchInput/SearchInput'
import { useDebounce } from '@/shared/hooks'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { OptionModel } from '@/shared/lib/types'
import { useIsMobile } from '@/shared/utils'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { DatePickerWithRange } from '@/entities/datePicker'
import { DateRange } from 'react-day-picker'
import { normalizeDateRangeForList } from '@/shared/lib'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'

const filterButtons: OptionModel[] = [
  { index: 0, label: 'Все', value: 'all' },
  { index: 1, label: 'Ожидают', value: 'waiting' },
  { index: 2, label: 'В работе', value: 'inProgress' },
  { index: 3, label: 'Оплаченные', value: 'paid' },
  { index: 4, label: 'Ошибки', value: 'failed' },
]

// Маппинг вкладок на статусы API
const statusMap: Record<string, TraderWithdrawFilterStatus> = {
  all: TraderWithdrawFilterStatus.All,
  waiting: TraderWithdrawFilterStatus.Waiting,
  inProgress: TraderWithdrawFilterStatus.InProgress,
  paid: TraderWithdrawFilterStatus.Paid,
  failed: TraderWithdrawFilterStatus.Failed,
}

export const Payouts = () => {
  const [selectedTab, setSelectedTab] = useState<
    'all' | 'waiting' | 'inProgress' | 'paid' | 'failed'
  >('all')
  const [searchId, setSearchId] = useState('')
  const debouncedSearch = useDebounce(searchId, 500)
  const [searchFilters, setSearchFilters] = useState<WithdrawsStateType | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [dateRange, setDateRange] = useState<DateRange | undefined>()
  const [sort, setSort] = useState<{
    sortCreatedAt?: SortDirection
    sortAmountTo?: SortDirection
    sortAllowedAt?: SortDirection
  }>({})

  const toggleSort = (key: keyof typeof sort) => {
    setSort((prev) => {
      const current = prev[key]
      let next: SortDirection | undefined
      if (current === undefined) next = SortDirection.Asc
      else if (current === SortDirection.Asc) next = SortDirection.Desc
      else next = undefined
      return { ...prev, [key]: next }
    })
  }

  const { modals, setters, actions } = useWithdrawsModal()
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const isMobile = useIsMobile()

  // Очистка поискового запроса от пробелов и спецсимволов
  const sanitizeSearchQuery = (query: string): string => {
    // Убираем все пробелы, +, -, (, ), и другие спецсимволы
    return query.replace(/[\s+\-()]/g, '')
  }

  // Очищенный поисковый запрос для отправки на сервер
  const sanitizedSearch = sanitizeSearchQuery(debouncedSearch)

  const { startDate, endDate } = normalizeDateRangeForList(dateRange)

  const {
    loading: { withdrawsIsLoad, isPendingCreateWithdraw },
    data: { skip, items, totalItems, withdrawsError },
    methods: { createWithdraw, handlePageChange },
  } = useWithdrawsMerchant({
    filters: {
      id: searchFilters?.id,
      currencyto: searchFilters?.currencyto,
      methodto: searchFilters?.methodto,
      bankto: searchFilters?.bankto,
      amountfrom: searchFilters?.amountfrom,
      amountto: searchFilters?.amountto,
      createdfromutc: searchFilters?.createdfromutc ?? '',
      createdtoutc: searchFilters?.createdtoutc ?? '',
      preq: searchFilters?.preq,
      withdrawStatus: searchFilters?.withdrawStatus,
      refreshTrigger,
    },
    onCreateSettled: actions.closeCreateModal,
  })

  // Трейдер - новый API с единым эндпоинтом
  const { data, errors, loading, methods, ui } = useWithdrawsTrader({
    status: statusMap[selectedTab],
    onSuccessDeclineCallback: () => {
      actions.closeCancelModal()
      actions.openConfirmationCancelModal()
    },
    debouncedSearch: sanitizedSearch,
    startDate,
    endDate,
    sortCreatedAt: sort.sortCreatedAt,
    sortAmountTo: sort.sortAmountTo,
    sortAllowedAt: sort.sortAllowedAt,
  })

  // Сброс пагинации при поиске
  useEffect(() => {
    if (sanitizedSearch) {
      methods.resetPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedSearch])

  // Сброс пагинации при изменении дат (не при первом рендере)
  useEffect(() => {
    // Сбрасываем пагинацию только если диапазон дат установлен
    if (dateRange?.from || dateRange?.to) {
      methods.resetPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange])

  // Сброс пагинации при изменении вкладки
  useEffect(() => {
    methods.resetPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  const handleTabChange = (value: 'all' | 'waiting' | 'inProgress' | 'paid' | 'failed') => {
    setSelectedTab(value)
  }

  const handleDateChange = (range: DateRange | undefined) => {
    setDateRange(range)
  }

  const tabPanelsTrader = {
    all: (
      <AssignedPanel
        isAll={true}
        assignedData={data.withdrawsData}
        assignedError={errors.withdrawsError}
        isAssignedDataLoading={loading.isWithdrawsLoading}
        loadingAssignedIds={ui.loadingAssignedIds}
        methods={methods}
        skip={data.effectiveSkip}
        sort={sort}
        toggleSort={toggleSort}
      />
    ),
    waiting: (
      <AssignedPanel
        assignedData={data.withdrawsData}
        assignedError={errors.withdrawsError}
        isAssignedDataLoading={loading.isWithdrawsLoading}
        loadingAssignedIds={ui.loadingAssignedIds}
        methods={methods}
        skip={data.effectiveSkip}
        sort={sort}
        toggleSort={toggleSort}
      />
    ),
    inProgress: (
      <AcceptedPanel
        acceptedWithdraws={data.withdrawsData}
        acceptedWithdrawsError={errors.withdrawsError}
        isAcceptedWithdrawsLoading={loading.isWithdrawsLoading}
        isConfirmWithdrawLoading={loading.isConfirmWithdrawLoading}
        isCancelWithdrawLoading={loading.isCancelWithdrawLoading}
        methods={methods}
        skip={data.effectiveSkip}
        sort={sort}
        toggleSort={toggleSort}
      />
    ),
    paid: (
      <CompletedPanel
        paidWithdrawsData={data.withdrawsData}
        isPaidWithdrawLoading={loading.isWithdrawsLoading}
        paidWithdrawsError={errors.withdrawsError}
        methods={methods}
        skip={data.effectiveSkip}
        sort={sort}
        toggleSort={toggleSort}
        showRate={true}
      />
    ),
    failed: (
      <CompletedPanel
        paidWithdrawsData={data.withdrawsData}
        isPaidWithdrawLoading={loading.isWithdrawsLoading}
        paidWithdrawsError={errors.withdrawsError}
        methods={methods}
        skip={data.effectiveSkip}
        sort={sort}
        toggleSort={toggleSort}
        showRate={false}
      />
    ),
    appeals: <div className="mt-8">Заявок нет.</div>,
  }

  if (userRole === Roles.Merchant) {
    return (
      <div>
        <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-6 mb-4 md:mb-8">
          <PageTitle title="Исходящие заявки" />
          <BaseButton
            variant="primary"
            padding="none"
            radius="md"
            className="px-[30px] w-full md:w-[250px] md:max-w-[250px]"
            iconPosition="left"
            icon={<Plus className="text-white" />}
            onClick={actions.openCreateModal}
          >
            <span className="hidden md:block">Создать новую</span>
            <span className="md:hidden">Создать</span>
          </BaseButton>
        </div>

        <FiltersWithdraws
          onSubmit={(filters) => {
            setSearchFilters(filters)
            setRefreshTrigger((prev) => prev + 1)
          }}
        />

        {withdrawsError ? (
          <p>Ошибка загрузки данных.</p>
        ) : withdrawsIsLoad ? (
          <BaseTableSkeleton />
        ) : !items || items.length === 0 ? (
          <p>Исходящих заявок нет.</p>
        ) : (
          <BaseTable
            data={items}
            columns={getColumns()}
            onPageChange={(newSkip) => handlePageChange(newSkip)}
            totalItems={totalItems}
            skip={skip}
            cardClasses="border-0"
            cardPadding="none"
            mobileCards={items?.map((withdraw) => (
              <MobileBlockMerchantWithdraw
                key={withdraw.id}
                withdraw={withdraw}
                userRole={userRole}
              />
            ))}
            isRowLink={userRole === Roles.Merchant}
          />
        )}

        <CreateWithdrawDialog
          modalVisible={modals.modalCreateVisible}
          setModalVisible={setters.setModalCreateVisible}
          onSubmit={createWithdraw}
          isLoading={isPendingCreateWithdraw}
        />
      </div>
    )
  }

  if (userRole === Roles.Trader) {
    return (
      <div>
        <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-6 mb-4 md:mb-8">
          <PageTitle title="Исходящие заявки" />
          <BaseButton
            onClick={() => setters.setModalEditPayoutVisible(true)}
            radius="lg"
            icon={<PenSettingsIcon className="text-white" />}
            iconPosition="right"
          >
            Настроить
          </BaseButton>
        </div>

        <PayoutsSettingsBanner
          data={data.balanceChannelsData}
          isLoading={loading.isBalanceChannelsLoading}
          isError={errors.balanceChannelsError}
        />

        <div className="flex md:flex-row flex-col gap-4 mb-4">
          <BaseTabs
            tabs={filterButtons}
            selectedTab={selectedTab}
            onSelect={handleTabChange}
            direction="row"
            variant="filter"
          />
          <div className="flex gap-4 md:ml-auto">
            <DatePickerWithRange onDateChange={handleDateChange} />

            <SearchInput
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              maxWidth={isMobile ? '100%' : '250px'}
              placeholder="ID, телефон, номер карты..."
            />
          </div>
        </div>

        {tabPanelsTrader[selectedTab]}

        <CreateWithdrawDialog
          modalVisible={modals.modalCreateVisible}
          setModalVisible={setters.setModalCreateVisible}
          onSubmit={createWithdraw}
          isLoading={isPendingCreateWithdraw}
        />

        {data.balanceChannelsData && (
          <EditPayoutDialog
            modalEdit={modals.modalEditPayoutVisible}
            setModalEdit={setters.setModalEditPayoutVisible}
            balance={data.balanceChannelsData.availableAmount}
          />
        )}

        <AppealsDialog
          modalAppeals={modals.modalAppeals}
          setModalAppeals={setters.setModalAppeals}
        />
      </div>
    )
  }
}
